import { useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, CopyCheck, Upload } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { CV_BANK_SOURCE_LABELS, type CvBankEntryView } from '@nest-recruit/shared';
import { Avatar } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { DataTable } from '../../../components/data-table/DataTable';
import { TableToolbar } from '../../../components/data-table/TableToolbar';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { EmptyState, FilteredEmptyState, ForbiddenState } from '../../../components/system-states/SystemState';
import { formatDate, formatNumber } from '../../../lib/format';
import { Popover } from '../../../components/Popover';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { EMPTY_SELECTION, isSelected, resolveSelection, selectionCount, type SelectionState } from '../../../lib/selection';
import { useCvBank, useCvBankDuplicates, useCvBankEntry } from '../api/cv-bank-api';
import { CvBankEntryDrawer } from '../components/CvBankEntryDrawer';
import { DuplicateChooser } from '../components/DuplicateChooser';
import { RemovePanel } from '../components/RemovePanel';
import { UploadDrawer } from '../components/UploadDrawer';
import { entrySteps } from '../cv-bank-format';
import { useCvBankAccess, useRemoveFromCvBank, useResolveDuplicate, type CvBankRemoval } from '../hooks/use-cv-bank-actions';
import styles from '../CvBank.module.css';
import fit from '../../../components/FitPage.module.css';

const PAGE_SIZE = 50;
const MIN_QUERY = 2;

/** ADR 0046: the search, page, open row and the duplicates section live in the URL (06-qol §6). */
export interface CvBankSearch {
  readonly q?: string;
  readonly page?: number;
  readonly entry?: string;
  readonly dupes?: boolean;
}

/**
 * The CV Bank (ADR 0046): every CV kept on purpose, searchable by name, email, phone or any word
 * in the CV. A row opens the CV beside the list; "N possible duplicates" opens the pairs, each
 * settled by keeping the first, the second or both. "Upload CVs" puts files straight in.
 */
export function CvBankPage(): ReactNode {
  const { canSee, canEdit } = useCvBankAccess();
  const navigate = useNavigate();
  const search = useSearch({ from: '/app-layout/cv-bank' }) as CvBankSearch;
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [isUploading, setIsUploading] = useState(false);
  const [bulkRemoving, setBulkRemoving] = useState<AnchorRect | null>(null);
  const query = search.q ?? '';
  const trimmed = query.trim();
  const page = search.page && search.page > 0 ? search.page : 1;

  const searched = trimmed.length >= MIN_QUERY ? trimmed : undefined;
  const bank = useCvBank({ page, pageSize: PAGE_SIZE, ...(searched ? { query: searched } : {}) });
  const onPage = search.entry ? (bank.data?.rows.find((row) => row.id === search.entry) ?? null) : null;
  // A link to an entry on another page (or a shared link) still opens it: fetched alone once the page has landed.
  const isOffPage = canSee && search.entry !== undefined && onPage === null && bank.data !== undefined && !bank.isPlaceholderData;
  const linked = useCvBankEntry(isOffPage && search.entry ? search.entry : null);
  const duplicates = useCvBankDuplicates(canSee);
  const remove = useRemoveFromCvBank();
  const resolve = useResolveDuplicate();

  if (!canSee) return <ForbiddenState resource="the CV Bank" contact="Your admin" />;

  const rows = bank.data?.rows ?? [];
  const total = bank.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pairs = duplicates.data ?? [];
  const opened = onPage ?? (isOffPage ? (linked.data ?? null) : null);
  const steps = onPage ? entrySteps(rows, onPage.id) : null;
  const selected = rows.filter((row) => isSelected(selection, row.id));
  const selectedCount = selectionCount(selection, total) ?? 0;

  function setSearch(next: CvBankSearch): void {
    void navigate({ to: '/cv-bank', search: next, replace: true });
  }

  const columns: readonly Column<CvBankEntryView>[] = [
    {
      id: 'name',
      header: 'Name',
      isIdentity: true,
      defaultWidth: 220,
      // A row opens its CV beside the list; the drawer links on to the profile.
      cell: (row) => (
        <button type="button" className={styles.nameButton} onClick={() => setSearch({ ...search, entry: row.id })}>
          <Avatar name={row.candidate.fullName} size="sm" />
          {row.candidate.fullName}
        </button>
      ),
      exportValue: (row) => row.candidate.fullName,
    },
    { id: 'lastJob', header: 'Last job applied to', defaultWidth: 220, cell: (row) => row.lastJobTitle },
    { id: 'addedBy', header: 'Added by', defaultWidth: 160, cell: (row) => row.addedByName },
    { id: 'addedAt', header: 'Added on', defaultWidth: 120, cell: (row) => formatDate(row.addedAt) },
    { id: 'cv', header: 'CV', defaultWidth: 220, cell: (row) => row.cv.fileName },
    { id: 'source', header: 'How added', defaultWidth: 150, cell: (row) => CV_BANK_SOURCE_LABELS[row.source] },
  ];

  return (
    <div className={`${styles.page} ${fit.fit}`}>
      <TableToolbar title="CV Bank" total={total} query={query} onQueryChange={(next) => setSearch({ ...search, q: next === '' ? undefined : next, page: undefined })} filters={[]} onClearFilters={() => setSearch({})} />
      <div className={styles.actions}>
        {pairs.length > 0 && (
          <button type="button" className={styles.flagButton} aria-expanded={search.dupes === true} onClick={() => setSearch({ ...search, dupes: search.dupes ? undefined : true })}>
            <CopyCheck size={16} aria-hidden />
            {pairs.length === 1 ? '1 possible duplicate' : `${formatNumber(pairs.length)} possible duplicates`}
          </button>
        )}
        {canEdit && (
          <Button variant="solid" icon={Upload} onClick={() => setIsUploading(true)}>
            Upload CVs
          </Button>
        )}
      </div>
      <p className={styles.hint}>Search by name, email, mobile number or any word in the CV. CVs stay here until someone removes them.</p>

      {search.dupes && pairs.length > 0 && (
        <section className={styles.duplicates} aria-label="Possible duplicates">
          <h2 className={styles.sectionTitle}>Possible duplicates</h2>
          {pairs.map((pair) => (
            <DuplicateChooser key={`${pair.first.id}-${pair.second.id}`} pair={pair} canEdit={canEdit} onChoose={(keep) => void resolve(pair, keep)} />
          ))}
        </section>
      )}

      <DataTable
        screenId="cv-bank"
        label="CV Bank"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        total={total}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        isSelectable={canEdit}
        onOpenRow={(row) => setSearch({ ...search, entry: row.id })}
        rowActions={(row) => (
          <Button variant="quiet" size="sm" onClick={() => setSearch({ ...search, entry: row.id })}>
            Open CV
          </Button>
        )}
        bulkActions={
          canEdit && selectedCount > 0 ? (
            <Button variant="quiet" size="sm" onClick={(event) => setBulkRemoving(anchorFrom(event.currentTarget))}>
              Remove from CV Bank
            </Button>
          ) : undefined
        }
        isLoading={bank.isPending}
        emptyState={
          trimmed !== '' ? (
            <FilteredEmptyState filterCount={1} onClearFilters={() => setSearch({})} />
          ) : (
            <EmptyState
              title="The CV Bank is empty"
              body='Add a good CV from a candidate’s profile, tick people in a list and press "Add to CV Bank", or upload CVs here.'
              action={
                canEdit ? (
                  <Button variant="solid" icon={Upload} onClick={() => setIsUploading(true)}>
                    Upload CVs
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />

      {pageCount > 1 && (
        <nav className={styles.pager} aria-label="Pages">
          <Button variant="ghost" icon={ChevronLeft} disabled={page <= 1} onClick={() => setSearch({ ...search, page: page - 1 })}>
            Previous
          </Button>
          <span className={styles.pageOf}>
            Page {page} of {pageCount}
          </span>
          <Button variant="ghost" disabled={page >= pageCount} onClick={() => setSearch({ ...search, page: page + 1 })}>
            Next
            <ChevronRight size={16} aria-hidden />
          </Button>
        </nav>
      )}

      <CvBankEntryDrawer
        entry={opened}
        canEdit={canEdit}
        onClose={() => setSearch({ ...search, entry: undefined })}
        steps={steps}
        onStep={(entryId) => setSearch({ ...search, entry: entryId })}
        onRemove={(entry, reason) => {
          setSearch({ ...search, entry: undefined });
          void remove({ kind: 'ids', entries: [{ id: entry.id, name: entry.candidate.fullName }] }, reason);
        }}
      />
      <UploadDrawer isOpen={isUploading} onClose={() => setIsUploading(false)} />
      <Popover anchor={bulkRemoving} onClose={() => setBulkRemoving(null)} align="left" width={360} label="Remove from CV Bank">
        {bulkRemoving && (
          <RemovePanel
            who={selectedCount === 1 && selected[0] ? selected[0].candidate.fullName : `${formatNumber(selectedCount)} people`}
            onCancel={() => setBulkRemoving(null)}
            onRemove={(reason) => {
              const removal = removalOf(selection, selected, searched, selectedCount);
              setBulkRemoving(null);
              setSelection(EMPTY_SELECTION);
              void remove(removal, reason);
            }}
          />
        )}
      </Popover>
    </div>
  );
}

/**
 * Ticked rows go by id; "Select all N matching" goes as the search itself, less the rows un-ticked,
 * so every match leaves — not only the page on screen (owner Q2 a, Group 8).
 */
export function removalOf(selection: SelectionState, selectedOnPage: readonly CvBankEntryView[], query: string | undefined, count: number): CvBankRemoval {
  const resolved = resolveSelection(selection);
  if (resolved.kind === 'ids') return { kind: 'ids', entries: selectedOnPage.map((row) => ({ id: row.id, name: row.candidate.fullName })) };
  return { kind: 'all-matching', query, excludedIds: resolved.excluded, count, visibleIds: selectedOnPage.map((row) => row.id) };
}
